import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";

export const ATLAS_MARK_STILL = "/assistant-mascot/atlas-mark.png";

function webglAvailable() {
  try {
    const probe = document.createElement("canvas");
    return Boolean(probe.getContext("webgl2") || probe.getContext("webgl"));
  } catch {
    return false;
  }
}

/**
 * La A de Atlas en 3D. Mientras se carga el 3D (en un fragmento aparte, para
 * no engordar la carga inicial del Portal), o si el navegador no puede
 * dibujarlo, se ve la misma A en imagen fija.
 *
 * `mode`: "reposo" respira y de vez en cuando mira alrededor o se alegra;
 * "pensando" gira la cabeza mientras el asistente prepara la respuesta.
 * `greet`: saluda con el brazo al aparecer. `interactive`: se puede girar arrastrando.
 */
export function AtlasMark({ size = 68, mode = "reposo", greet = false, interactive = false, glow = true }) {
  const canvasRef = useRef(null);
  const markRef = useRef(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !webglAvailable()) return undefined;
    let cancelled = false;
    const lost = (event) => {
      event.preventDefault();
      markRef.current?.dispose();
      markRef.current = null;
      setLive(false);
    };
    canvas.addEventListener("webglcontextlost", lost);
    import("./createAtlasMark.js").then(({ createAtlasMark }) => {
      if (cancelled) return;
      try {
        markRef.current = createAtlasMark(canvas, { interactive, maxPixelRatio: 3 });
        markRef.current.setMode(mode);
        setLive(true);
        if (greet) window.setTimeout(() => markRef.current?.play("saludo"), 500);
      } catch {
        markRef.current = null;
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
      canvas.removeEventListener("webglcontextlost", lost);
      markRef.current?.dispose();
      markRef.current = null;
    };
    // Mode changes are applied below without recreating the scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, greet]);

  useEffect(() => { markRef.current?.setMode(mode); }, [mode, live]);

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        filter: glow
          ? "drop-shadow(0 6px 12px rgba(70, 58, 134, .32)) drop-shadow(0 0 10px rgba(42, 208, 209, .22))"
          : "none",
        "& > *": { position: "absolute", inset: 0, width: "100%", height: "100%" },
      }}
    >
      <Box component="img" src={ATLAS_MARK_STILL} alt="" sx={{ objectFit: "contain", opacity: live ? 0 : 1, transition: "opacity 240ms" }} />
      <Box component="canvas" ref={canvasRef} sx={{ display: "block", opacity: live ? 1 : 0, transition: "opacity 240ms" }} />
    </Box>
  );
}
